/**
 * What to tell someone whose WhatsApp connection did not go through.
 *
 * Most failures here are Meta's and their message is the useful one, so it is
 * passed straight through. The exception is a WhatsApp account that another
 * organisation has already linked: Echo refuses it deliberately, and "Connection
 * Failed" alone would send the customer looking for a fault in Meta Business
 * Manager that does not exist. That case gets a title saying it is already in
 * use and a next step that actually resolves it.
 *
 * The organisation holding the account is never named — that would tell one
 * customer about another. Support can find it in the Cloud Functions logs.
 */

export interface WhatsAppLinkError {
    /** Heading for the modal. */
    title: string;
    /** Body text. */
    text: string;
}

const DEFAULT_MESSAGE = 'Failed to connect WhatsApp Business Account';

/**
 * Turn a failed connect callable into something worth reading.
 *
 * @param error The error the callable rejected with.
 */
export function explainWhatsAppLinkError(error: any): WhatsAppLinkError {
    // Callable errors arrive as 'functions/<code>'; the guard throws already-exists.
    const code: string = error?.code ?? '';

    if (code.endsWith('already-exists')) {
        return {
            title: 'Already Linked Elsewhere',
            text: error?.message ||
                'This WhatsApp Business Account is already linked to another organisation in Echo.'
        };
    }

    if (error?.details?.reason === 'different-waba-connected') {
        return {
            title: 'Already Connected',
            text: error?.message ||
                'This organisation is already connected to a different WhatsApp Business Account. ' +
                'Disconnect it in Organisation Settings before connecting another.'
        };
    }

    if (code.endsWith('permission-denied')) {
        return {
            title: 'Not Your Organization',
            text: error?.message ||
                'You do not have access to connect WhatsApp for this organization.'
        };
    }

    return {
        title: 'Connection Failed',
        text: error?.message || DEFAULT_MESSAGE
    };
}
